import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { http } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Shield, Check, X, Mail, Clock, Building2, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function AdminClaims() {
  const { user, lang, loading } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, pending_verification: 0 });
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const { data } = await http.get("/admin/claims");
      setItems(data.items || []);
      setCounts({ pending: data.pending, pending_verification: data.pending_verification });
    } catch (e) {
      // Surface network / permission failures instead of swallowing them —
      // admins need to know when the queue is stale.
      toast.error(e?.response?.data?.detail || (lang === "fr" ? "Chargement impossible" : "Load failed"));
    }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    if (user.user_type !== "admin") { nav("/"); return; }
    load();
  }, [user, loading, nav]);

  const decide = async (claimId, approve) => {
    setBusy(claimId);
    try {
      await http.post(`/admin/claims/${claimId}/decide`, { approve });
      toast.success(approve
        ? (lang === "fr" ? "Demande approuvée" : "Claim approved")
        : (lang === "fr" ? "Demande refusée" : "Claim rejected"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (lang === "fr" ? "Erreur" : "Error"));
    } finally {
      setBusy(null);
    }
  };

  const filtered = filter === "all" ? items : items.filter((c) => c.status === filter);

  const statusBadge = (s) => {
    const map = {
      pending_verification: { label: lang === "fr" ? "Email en attente" : "Email pending", bg: "bg-amber-100 text-amber-800" },
      pending: { label: lang === "fr" ? "À valider" : "To review", bg: "bg-orange-100 text-orange-800" },
      approved: { label: lang === "fr" ? "Approuvée" : "Approved", bg: "bg-emerald-100 text-emerald-800" },
      rejected: { label: lang === "fr" ? "Refusée" : "Rejected", bg: "bg-slate-200 text-slate-700" },
    };
    const m = map[s] || map.pending;
    return <span data-testid={`claim-status-${s}`} className={`text-xs px-2 py-0.5 rounded-full font-medium ${m.bg}`}>{m.label}</span>;
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center gap-2 mb-6">
          <Shield className="w-5 h-5 text-[#F97316]" />
          <h1 data-testid="admin-claims-title" className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#1E3A8A]">
            {lang === "fr" ? "Revendications d'entreprises" : "Company claims"}
          </h1>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {[
            { k: "all", label: lang === "fr" ? "Toutes" : "All", count: items.length },
            { k: "pending", label: lang === "fr" ? "À valider" : "To review", count: counts.pending },
            { k: "pending_verification", label: lang === "fr" ? "Email en attente" : "Email pending", count: counts.pending_verification },
            { k: "approved", label: lang === "fr" ? "Approuvées" : "Approved" },
            { k: "rejected", label: lang === "fr" ? "Refusées" : "Rejected" },
          ].map((f) => (
            <button
              key={f.k}
              data-testid={`claims-filter-${f.k}`}
              onClick={() => setFilter(f.k)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                filter === f.k
                  ? "bg-[#1E3A8A] text-white"
                  : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
              }`}
            >
              {f.label}
              {f.count !== undefined && f.count > 0 && (
                <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] ${filter === f.k ? "bg-white/20" : "bg-slate-100"}`}>{f.count}</span>
              )}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div data-testid="claims-empty" className="text-center py-12 text-sm text-slate-500 bg-white rounded-2xl border border-slate-200">
            {lang === "fr" ? "Aucune revendication dans cette catégorie." : "No claim in this category."}
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((c) => (
              <div key={c.id} data-testid={`claim-${c.id}`} className="rounded-2xl bg-white border border-slate-200 p-5 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link to={`/vitrine/${c.company_id}`} className="font-display text-lg font-semibold text-[#1E3A8A] hover:underline flex items-center gap-1">
                        <Building2 className="w-4 h-4" /> {c.company_name}
                        <ExternalLink className="w-3 h-3 opacity-60" />
                      </Link>
                      {statusBadge(c.status)}
                      {c.domain_match && (
                        <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                          {lang === "fr" ? "Domaine confirmé" : "Domain match"}
                        </Badge>
                      )}
                      {c.auto_approved && (
                        <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">Auto</Badge>
                      )}
                    </div>
                    <div className="mt-2 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                      <p className="text-slate-700"><span className="text-slate-500">{lang === "fr" ? "Demandeur:" : "Requester:"}</span> <b>{c.user_name}</b></p>
                      <p className="text-slate-700 flex items-center gap-1">
                        <Mail className="w-3.5 h-3.5 text-slate-400" /> {c.contact_email}
                        {c.verified_at && <Check className="w-3.5 h-3.5 text-emerald-600" title={lang === "fr" ? "Email vérifié" : "Email verified"} />}
                      </p>
                      {c.role_at_company && (
                        <p className="text-slate-700"><span className="text-slate-500">{lang === "fr" ? "Rôle:" : "Role:"}</span> {c.role_at_company}</p>
                      )}
                      <p className="text-slate-500 flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" /> {new Date(c.created_at).toLocaleString()}
                      </p>
                    </div>
                    {c.message && (
                      <p className="text-sm text-slate-600 mt-2 italic border-l-2 border-slate-200 pl-3">« {c.message} »</p>
                    )}
                  </div>
                  {c.status === "pending" && (
                    <div className="flex gap-2 shrink-0">
                      <Button
                        data-testid={`claim-approve-${c.id}`}
                        onClick={() => decide(c.id, true)}
                        disabled={busy === c.id}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full text-xs h-9 px-4"
                      >
                        {busy === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}
                        {lang === "fr" ? "Approuver" : "Approve"}
                      </Button>
                      <Button
                        data-testid={`claim-reject-${c.id}`}
                        onClick={() => decide(c.id, false)}
                        disabled={busy === c.id}
                        variant="outline"
                        className="border-slate-300 text-slate-700 hover:bg-slate-50 rounded-full text-xs h-9 px-4"
                      >
                        <X className="w-3.5 h-3.5 mr-1" /> {lang === "fr" ? "Refuser" : "Reject"}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
